import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getLangFromPath,
  withLang,
  stripLang,
  SUPPORTED_LANGS,
  DEFAULT_LANG,
} from "./routing";

/**
 * Bidirectional sync between URL path-prefix language and i18next state.
 *
 * - On mount / route change: URL prefix → i18next.changeLanguage()
 * - When i18next.language changes via the LanguageSwitcher: navigate to the
 *   prefixed path while preserving the rest of the URL (search, hash).
 *
 * Place this once inside <BrowserRouter>, above the route tree.
 */
export default function LangSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  // URL → i18n
  useEffect(() => {
    const urlLang = getLangFromPath(location.pathname);
    const activeLang = (i18n.language || "nl").split("-")[0];
    if (SUPPORTED_LANGS.includes(urlLang) && urlLang !== activeLang) {
      i18n.changeLanguage(urlLang);
      document.documentElement.lang = urlLang;
    } else if (urlLang === activeLang) {
      document.documentElement.lang = urlLang;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // i18n → URL (only when user actively switches language, not on initial load)
  useEffect(() => {
    const handler = (newLang) => {
      const clean = (newLang || DEFAULT_LANG).split("-")[0];
      if (!SUPPORTED_LANGS.includes(clean)) return;
      const urlLang = getLangFromPath(location.pathname);
      if (urlLang === clean) return; // already aligned
      const path = stripLang(location.pathname);
      const target = withLang(path, clean) + location.search + location.hash;
      navigate(target, { replace: true });
    };
    i18n.on("languageChanged", handler);
    return () => i18n.off("languageChanged", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, location.hash]);

  return null;
}
